import PropTypes from 'prop-types'
import React from 'react'
import classnames from 'classnames'

import Link from './Link'
import Icon from './Icon'

const BUTTON_VARIATIONS = [
  'secondary',
  'light',
  'blue',
  'form', // almost identical to blue
  'sky',
  'outline-blue',
  'outline-deep-night-blue',
  'plain',
  // product variations
  'flow',
  'commands',
  'satellite',
  // helper variations
  'red--pastel',
  'orange--pastel',
  'yellow',
  'green--pastel',
  'purple--pastel',
  'blue--pastel'
]

// prevent creating new function every time default prop onClick is required
const noop = () => {}

function Button (props) {
  const {
    children,
    variation,
    extraClass,
    iconExtraClass,
    disabled,
    download,
    href,
    onClick,
    target,
    forceAnchorElement,
    type,
    multiLine,
    ariaLabel,
    // used by action buttons
    action,
    square,
    largeSquare,
    selected,
    countdown
  } = props
  const icon =
    props.icon !== undefined ? props.icon : href ? 'arrow-in-circle' : undefined
  const classes = classnames(
    // NOTE: multi-line only used together with icon, to show icon on second line
    {
      'button--multiline': multiLine && icon,
      // only for action buttons
      'button--selected': action && selected,
      'button--countdown': action && countdown,
      'button--square': action && square,
      'button--square-large': action && largeSquare
    },
    `button-${variation}`,
    extraClass
  )
  const iconContent = icon && (
    <Icon
      icon={icon}
      className={classnames(
        {
          ml1: !multiLine && !!children
        },
        iconExtraClass,
        'icon button__icon'
      )}
    />
  )
  const countdownContent = countdown && (
    <div className='button__countdown'>
      <div
        style={{ animationDuration: countdown / 1000 + 's' }}
        className='button__countdown__progress'
      />
    </div>
  )

  if (href) {
    return (
      <Link
        href={href}
        target={target}
        forceAnchorElement={forceAnchorElement}
        className={classes}
        download={download}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {countdown && countdownContent}
        {children}
        {iconContent}
      </Link>
    )
  }
  return (
    <button
      disabled={disabled}
      download={download}
      type={type}
      className={classes}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {countdown && countdownContent}
      {children}
      {iconContent}
    </button>
  )
}

Button.propTypes = {
  children: PropTypes.any,
  variation: PropTypes.oneOf(BUTTON_VARIATIONS).isRequired,
  extraClass: PropTypes.string,
  disabled: PropTypes.bool,
  href: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  target: PropTypes.string,
  type: PropTypes.string,
  icon: PropTypes.string,
  /** action buttons have different behavior and specific props */
  action: PropTypes.bool,
  /** only for action buttons */
  selected: PropTypes.bool,
  /** only for action buttons */
  square: PropTypes.bool,
  /** only for action buttons */
  largeSquare: PropTypes.bool,
  /** ms, countdown animation duration. Only for action buttons */
  countdown: PropTypes.number,
  ariaLabel: (props, propName, componentName) => {
    const { icon, children, ariaLabel } = props
    if (icon && !children && !ariaLabel) {
      return new Error(
        'The prop `' +
          propName +
          '` is required in' +
          ' `' +
          componentName +
          '` when using `icon && !children`, i.e. iconOnly, but its value is `' +
          props[propName] +
          '`.'
      )
    }
  }
}

Button.defaultProps = {
  onClick: noop,
  type: 'button',
  variation: 'secondary'
}

export default Button
